"use client";

// WO-GF-001 B4.3 → S16 — transport search-widget hydrator. Document-scoped, same family as
// MapLayer / ScheduleMapLayer / MarketplaceCartLayer: it finds the CAPTURED (dead-photocopy) transport
// search form in the mirrored body and wires the real capability onto it WITHOUT touching its DOM at
// rest, so the pixel gate stays green.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────────
// S16 · RE-WALK-4 (F92–F99). Every row below was verified against LIVE snowbus.com read-only on
// 2026-07-22 (and, for the zone model, against the legacy `route` table itself) BEFORE any code moved.
//
//   LIVE (/mountain-whistler)                              BUILD BEFORE S16
//   ───────────────────────────────────────────────────    ────────────────────────────────────────
//   From  1 option  "Vancouver & YVR Airport" value=van…   22 STOPS (burrard-skytrain, …)   ← F93
//   To    1 option  "Whistler"                value=whi…   23 MOUNTAINS                     ← F93
//   Depart/Return   react-datepicker month popper          inert (no calendar, no map)      ← F94
//   Quantity        .memberPopover Adults/Children/Seniors  inert                            ← F95
//   Roundtrip/OneWay  .SearchWidget_switchbutton, active
//                     span carries SearchWidget_Active__*   inert                            ← F96
//   Search          → in-app schedule listing for the pair  → window.location = snowbus.com  ← F92
//
//   F93 · THE ZONE MODEL (the founder's "check how snowbus.com runs it on the backend"). The legacy
//   `route` table IS the model: `source_code`/`destination_code` are ZONE codes (`vancouver` =
//   "Vancouver & YVR Airport", `whistler`), and the 22 `journey_stop` rows are a LOWER layer picked
//   later in the funnel. Live shows exactly ONE option per side because the options are constrained to
//   the counterpart of an ACTIVE route — so the widget now offers `data.routes` (zone pairs), never
//   `data.stops` / `data.mountains`. Nothing invented: same query, same rows, correct layer.
//
//   F92 · Search now navigates IN-APP to the schedule page this build already serves for the chosen
//   pair (`/mountain-whistler/vancouver-to-whistler`), resolved from the mirror registry via
//   `getRouteSlugs` so it can never dangle. The legacy handoff-A deep-link is retired for Search.
// ─────────────────────────────────────────────────────────────────────────────────────────────────
//
//   • From / To → anchored ZONE dropdown (RW-15(d) founder override kept: a dropdown, not the big map).
//   • Swap arrow → flips From/To, exactly as live does.
//   • Depart / Return → the shared CalendarPopover (same calendar as the marketplace date fields).
//   • Quantity → live's Adults/Children/Seniors counter popover; the captured digit shows the total.
//   • Roundtrip / One Way → moves live's own `SearchWidget_Active__*` class; One Way disables Return.
//   • F98 close behaviour → every overlay closes on click-off OR Escape, uniformly.
//
// The map modals are NOT deleted — they stay reachable behind `window.__transportMapModal = true`
// (the CarouselLayer `__carouselAutoplay` idiom).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PortMap, { type PortMapMarker } from "@/components/map/PortMap";
import { ModalShell, btn } from "@/components/site/ModalShell";
import CalendarPopover, { type CalendarAnchor, startOfDay } from "@/components/site/CalendarPopover";
import { mountainHubHref } from "@/components/site/hub-nav";
import { openMountainSelect } from "@/components/site/MountainSelectLayer";
import type { TransportData, TransportLocation } from "@/lib/transport-adapter";
import { trackSearch } from "@/lib/analytics-event";

type ModalState = { kind: "location" | "mountain" } | null;
type DropdownState = { kind: "from" | "to"; x: number; y: number; w: number } | null;
type CalState = { field: "depart" | "return"; input: HTMLInputElement; anchor: CalendarAnchor } | null;
// S29 · F173 — the popover is ANCHORED to its field, so it needs the field's whole box, not the two
// numbers the old `{x, y}` carried. With only a left edge and a bottom, the old popover could not
// right-align, could not know how wide its field was, and could not tell whether it was covering
// anything — which is how it ended up hand-offset 120px to the left, straight over the SEARCH button.
type PaxAnchorRect = { left: number; right: number; top: number; bottom: number; width: number };
type PaxState = PaxAnchorRect | null;

type Pax = { adult: number; child: number; senior: number };
const DEFAULT_PAX: Pax = { adult: 1, child: 0, senior: 0 };

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Live's display format for the Depart/Return fields, e.g. "Wed Jul 22, 2026". */
function fmtLive(d: Date): string {
  return `${WD[d.getDay()]} ${MO[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Parse that same format back to a LOCAL date (never Date.parse — that drifts across the dateline). */
function parseLive(v: string): Date | null {
  const m = /^[A-Za-z]{3}\s+([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4})$/.exec((v || "").trim());
  if (!m) return null;
  const mi = MO.indexOf(m[1]!);
  if (mi < 0) return null;
  const d = new Date(Number(m[3]), mi, Number(m[2]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** ISO yyyy-mm-dd, for the query string we carry into the schedule page (live's own param shape). */
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function labelFor(list: TransportLocation[], code: string): string {
  return list.find((l) => l.code === code)?.name || code;
}

export default function TransportSearchLayer({
  data,
  surface,
  routeSlugs = [],
}: {
  data: TransportData;
  surface?: string;
  /** `{from}-to-{to}` slugs this build serves for the mountain (F92 — never build an unserved URL). */
  routeSlugs?: string[];
}) {
  const [from, setFrom] = useState(data.defaultFrom);
  // `data.defaultTo` is the mountain's SHORT NAME ("Whistler"), not a zone code — using it directly made
  // the Search slug `vancouver-to-Whistler`, which is not a served route page, so Search silently fell back
  // to the hub. Seed from the zone that an ACTIVE route out of defaultFrom actually reaches.
  const [to, setTo] = useState(
    () => data.routes.find((r) => r.from === data.defaultFrom)?.to || data.defaultTo.toLowerCase(),
  );
  const [modal, setModal] = useState<ModalState>(null);
  const [dropdown, setDropdown] = useState<DropdownState>(null);
  const [cal, setCal] = useState<CalState>(null);
  const [paxAnchor, setPaxAnchor] = useState<PaxState>(null);
  const [pax, setPax] = useState<Pax>(DEFAULT_PAX);
  const [roundtrip, setRoundtrip] = useState(true);
  // Depart / Return live in refs (they are written into the CAPTURED inputs, which stay the source of
  // truth for what the user sees) plus state so Search can read them synchronously.
  const [depart, setDepart] = useState<Date | null>(null);
  const [ret, setRet] = useState<Date | null>(null);
  const departRef = useRef<HTMLInputElement | null>(null);
  const returnRef = useRef<HTMLInputElement | null>(null);

  const routeSet = useMemo(() => new Set(routeSlugs), [routeSlugs]);

  // ── F121 — BOTH ZONES ON BOTH SIDES (founder override of F93, ruled 2026-07-23) ─────────────────
  //
  // F93 read live's backend faithfully: each side was constrained to the counterpart of an ACTIVE route,
  // which is exactly why live's own widget shows a single option per side. The founder overrode that —
  // a traveller going Whistler→Vancouver should not have to find a different entry point than one going
  // Vancouver→Whistler. So both zones are offered on BOTH sides, either direction, and the only rule
  // enforced is that you cannot travel from a place to itself.
  //
  // This is a DIRECTED DIVERGENCE FROM LIVE, recorded as such (the F111/F117 class) — not a correction
  // of F93's reading, which stands as the accurate description of live's behaviour.
  //
  // The zone POOL is still derived from real route rows: the union of both endpoints of every active
  // route, resolved against the served location lists. Nothing is invented — a zone the legacy route
  // table never mentions cannot appear here.
  const zonePool = useMemo(() => {
    const byCode = new Map<string, TransportLocation>();
    for (const l of [...data.from, ...data.to]) if (!byCode.has(l.code)) byCode.set(l.code, l);
    const codes = new Set<string>();
    for (const r of data.routes) {
      codes.add(r.from);
      codes.add(r.to);
    }
    const pool = [...codes].map((c) => byCode.get(c)).filter((l): l is TransportLocation => !!l);
    // no route rows (a mountain with no pairs) → fall back to the served lists, as F93 did.
    return pool.length ? pool : [...byCode.values()];
  }, [data.routes, data.from, data.to]);

  // BOTH sides offer the WHOLE pool — that is the "both zones, either/or" half of the ruling. Filtering
  // the other side's zone out of the list would look like the override had been applied while leaving
  // the traveller exactly where F93 left them: with only two zones in play, exclusion collapses each
  // list back to a single option and the direction still cannot be reversed from either field.
  //
  // The "same-zone exclusion" half is enforced on SELECTION instead: picking the zone the other side
  // holds SWAPS the pair (see onPick), so the widget can never arrive at from === to, and choosing
  // Whistler in the From field simply turns a Vancouver→Whistler trip into Whistler→Vancouver.
  const fromOptions = zonePool;
  const toOptions = zonePool;

  const closeAll = useCallback(() => {
    setDropdown(null);
    setCal(null);
    setPaxAnchor(null);
  }, []);

  const submitSearch = useCallback((requireReturn: boolean) => {
    const slug = `${from}-to-${to}`;
    // The captured form has real date inputs. The generated design owns its
    // own calendar state, so its adapter submits the confirmed route without
    // manufacturing a second date model here.
    if (requireReturn) {
      if (roundtrip && !ret) {
        const input = returnRef.current;
        if (input) {
          const r = input.getBoundingClientRect();
          setDropdown(null);
          setPaxAnchor(null);
          setCal({ field: "return", input, anchor: { top: r.bottom + window.scrollY, left: r.left + window.scrollX } });
        }
        return;
      }
    }
    const params = new URLSearchParams();
    if (depart) params.set("startDate", iso(depart));
    if (requireReturn && roundtrip && ret) params.set("endDate", iso(ret));
    params.set("adult", String(pax.adult));
    params.set("child", String(pax.child));
    params.set("senior", String(pax.senior));
    const served = routeSet.has(slug);
    const href = served
      ? `/mountain-${data.mountain}/booking/schedule/depart/${slug}?${params}`
      : mountainHubHref(data.mountain);
    if (served) trackSearch(slug);
    document.documentElement.setAttribute("data-last-search", href);
    window.location.href = href;
  }, [data.mountain, depart, from, pax, ret, routeSet, roundtrip, to]);

  // ── wire the captured form ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const forms = Array.from(document.querySelectorAll<HTMLFormElement>("form")).filter(
      (f) => f.querySelector(".mountainPageForm") || f.querySelector("[class*='btnPrimary']"),
    );
    const roots: HTMLElement[] = forms;
    const cleanups: Array<() => void> = [];
    const on = (el: Element, ev: string, h: EventListener) => {
      el.addEventListener(ev, h);
      cleanups.push(() => el.removeEventListener(ev, h));
    };

    // The generated design OWNS its Search, zone menus and swap (lFromZone/lToZone, depDate, retDate,
    // roundTrip, pax). This layer used to intercept those clicks and submit from its OWN
    // from/to/depart/pax, which are never written by the design's calendar and traveller pickers, so
    // a landing search for Oct 21 / 3 riders navigated with no startDate and one adult. The design's
    // sgFunnelUrl is the single search authority (it also owns the GA4 `search` announcement), so
    // here the design roots are only marked as hydrated and no listener is attached to them.
    const bindDesign = () => {
      for (const field of Array.from(document.querySelectorAll<HTMLButtonElement>("[data-zone-label]"))) {
        const root = field.closest<HTMLElement>("[data-sg-menu]")?.parentElement?.parentElement;
        if (!root) continue;
        root.setAttribute("data-transport-hydrated", "true");
        root.setAttribute("data-transport-source", data.source);
      }
    };
    bindDesign();
    // DesignSurface can replace its SSR subtree when its own class runtime
    // recovers from a hydration mismatch (desktop bar and cloned mobile sheet); re-mark the fresh roots.
    const designObserver = new MutationObserver(bindDesign);
    designObserver.observe(document.body, { childList: true, subtree: true });

    for (const root of roots) {
      root.setAttribute("data-transport-hydrated", "true");
      root.setAttribute("data-transport-source", data.source);
      const form = root as HTMLFormElement;

      // ---- From / To zone dropdowns (matched by their sibling <label> text) ----
      for (const box of Array.from(form.querySelectorAll<HTMLElement>(".input-text-search"))) {
        const label = box.querySelector(".form-label")?.textContent?.trim().toLowerCase();
        const toggle = box.querySelector<HTMLButtonElement>("button.dropdown-toggle, .dropdown-toggle");
        if (!toggle || (label !== "from" && label !== "to")) continue;
        const kind = label as "from" | "to";
        toggle.textContent = kind === "from" ? labelFor(data.from, from) : labelFor(data.to, to);
        toggle.setAttribute("data-behavior", `transport-${kind}`);
        on(toggle, "click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if ((window as unknown as { __transportMapModal?: boolean }).__transportMapModal === true) {
            setModal({ kind: kind === "from" ? "location" : "mountain" });
            return;
          }
          // VIEWPORT coords: the menu renders inside a position:fixed overlay, so scrollX/scrollY must
          // NOT be added (that double-counts the scroll and detaches the menu from its field).
          const r = toggle.getBoundingClientRect();
          setCal(null);
          setPaxAnchor(null);
          setDropdown({ kind, x: r.left, y: r.bottom, w: Math.min(Math.max(r.width, 240), 360) });
        });
      }

      // ---- swap arrow (live flips From ↔ To) ----
      const arrow = form.querySelector<HTMLElement>(".top-search-change img, [class*='TopBookingBar_arrow']");
      if (arrow) {
        arrow.setAttribute("data-behavior", "transport-swap");
        (arrow as HTMLElement).style.cursor = "pointer";
        on(arrow, "click", (e) => {
          e.preventDefault();
          setFrom(to);
          setTo(from);
          closeAll();
        });
      }

      // ---- F94 Depart / Return → the shared calendar ----
      for (const box of Array.from(form.querySelectorAll<HTMLElement>(".input-text-search-2"))) {
        const label = box.querySelector(".form-label")?.textContent?.trim().toLowerCase();
        const input = box.querySelector<HTMLInputElement>("input");
        if (!input || (label !== "depart" && label !== "return")) continue;
        const field = label as "depart" | "return";
        if (field === "depart") departRef.current = input;
        else returnRef.current = input;
        input.setAttribute("data-behavior", `transport-date-${field}`);
        input.style.cursor = "pointer";
        const open = (e: Event) => {
          e.preventDefault();
          // One Way disables Return, exactly as live marks it `notAllowed`.
          if (field === "return" && !roundtrip) return;
          const r = input.getBoundingClientRect();
          setDropdown(null);
          setPaxAnchor(null);
          setCal({ field, input, anchor: { top: r.bottom + window.scrollY, left: r.left + window.scrollX } });
        };
        on(input, "mousedown", open);
        on(input, "focus", open);
      }

      // ---- F95 quantity → live's Adults / Children / Seniors popover ----
      const digit = form.querySelector<HTMLElement>("[class*='TopBookingBar_userdigit']");
      if (digit) {
        digit.setAttribute("data-behavior", "transport-pax");
        digit.style.cursor = "pointer";
        on(digit, "click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const r = digit.getBoundingClientRect();
          setDropdown(null);
          setCal(null);
          // VIEWPORT coords (no scrollX/scrollY) — the popover renders inside a position:fixed overlay,
          // exactly as the zone dropdown above notes.
          setPaxAnchor({ left: r.left, right: r.right, top: r.top, bottom: r.bottom, width: r.width });
        });
      }

      // ---- F96 Roundtrip / One Way ----
      const sw = form.querySelector<HTMLElement>("[class*='SearchWidget_switchbutton']")
        || document.querySelector<HTMLElement>("[class*='SearchWidget_switchbutton']");
      if (sw && sw.children.length >= 2) {
        sw.setAttribute("data-behavior", "transport-triptype");
        const [rt, ow] = [sw.children[0] as HTMLElement, sw.children[1] as HTMLElement];
        // live marks the ACTIVE span with its own `SearchWidget_Active__*` class — read the real token
        // off whichever span currently has it rather than hard-coding the build hash.
        const activeCls =
          Array.from(rt.classList).find((c) => c.includes("SearchWidget_Active")) ||
          Array.from(ow.classList).find((c) => c.includes("SearchWidget_Active"));
        for (const [el, wantRound] of [[rt, true], [ow, false]] as const) {
          el.style.cursor = "pointer";
          on(el, "click", (e) => {
            e.preventDefault();
            setRoundtrip(wantRound);
            if (activeCls) {
              rt.classList.toggle(activeCls, wantRound);
              ow.classList.toggle(activeCls, !wantRound);
            }
          });
        }
      }

      // ---- S18 · F129 Search → THE BOOKING FUNNEL (supersedes F92's schedule-page landing) ----
      //
      // F92 kept Search in-app by landing it on the served SEO route page for the zone pair. That half
      // was right and stays right — but the founder's F129 is that the destination "goes nowhere", and
      // he was correct: that page renders ZERO schedule rows. The port was faithful (live's own page is
      // equally empty); live's Search never went there at all, it handed off to the booking funnel.
      //
      // The funnel is now ported (F129), so Search lands on it: live's own URL contract, and a page that
      // actually answers the question the traveller asked. "Stays in-app" is preserved AND now
      // "returns results" — the two halves the old check conflated.
      const onSubmit = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        submitSearch(true);
      };
      on(form, "submit", onSubmit);
      const submitBtn = form.querySelector<HTMLButtonElement>("button[type='submit']");
      if (submitBtn) on(submitBtn, "click", onSubmit);
    }
    return () => {
      designObserver.disconnect();
      cleanups.forEach((c) => c());
      // React development strict mode intentionally runs effect cleanup before
      // mounting it again. Keep the DOM marker aligned with its listeners so
      // the second pass rebinds instead of inheriting a stale marker.
      for (const root of Array.from(document.querySelectorAll<HTMLElement>("[data-transport-hydrated]"))) {
        root.removeAttribute("data-transport-hydrated");
        root.removeAttribute("data-transport-source");
        for (const control of Array.from(root.querySelectorAll<HTMLElement>('[data-behavior^="transport-"]'))) {
          control.removeAttribute("data-behavior");
        }
      }
    };
  }, [data, from, to, roundtrip, depart, ret, pax, routeSet, closeAll, fromOptions, toOptions, submitSearch]);

  // Keep the captured digit in sync with the pax total (live shows the total in that span).
  const paxTotal = pax.adult + pax.child + pax.senior;
  useEffect(() => {
    const digit = document.querySelector<HTMLElement>("[class*='TopBookingBar_userdigit']");
    if (digit && digit.textContent !== String(paxTotal)) digit.textContent = String(paxTotal);
  }, [paxTotal]);

  // One Way: mark Return unusable the way live does (`notAllowed`), and clear the carried value.
  useEffect(() => {
    const box = document.querySelector<HTMLElement>(".input-text-return-search");
    if (box) box.classList.toggle("notAllowed", !roundtrip);
    if (!roundtrip) setRet(null);
  }, [roundtrip]);

  const marker = <span data-transport-layer={surface} data-transport-source={data.source} hidden />;

  return (
    <>
      {marker}
      {dropdown &&
        createPortal(
          <LocationDropdown
            kind={dropdown.kind}
            x={dropdown.x}
            y={dropdown.y}
            w={dropdown.w}
            options={dropdown.kind === "from" ? fromOptions : toOptions}
            selected={dropdown.kind === "from" ? from : to}
            onPick={(code) => {
              // F121 — SAME-ZONE EXCLUSION BY SWAP. Both fields offer both zones, so a traveller can
              // pick the zone the other field already holds; that means "go the other way", not "go
              // from a place to itself". Reversing the pair is the only reading that leaves a valid
              // trip, and it is what makes the direction changeable from EITHER field.
              if (dropdown.kind === "from") {
                if (code === to) setTo(from);
                setFrom(code);
              } else {
                if (code === from) setFrom(to);
                setTo(code);
              }
              setDropdown(null);
            }}
            onClose={() => setDropdown(null)}
          />,
          document.body,
        )}

      {cal &&
        createPortal(
          <CalendarPopover
            anchor={cal.anchor}
            selected={(cal.field === "depart" ? depart : ret) || parseLive(cal.input.value)}
            // A return cannot precede the departure.
            minDate={cal.field === "return" && depart ? depart : undefined}
            ignoreEl={cal.input}
            label={cal.field === "depart" ? "Choose departure date" : "Choose return date"}
            onPick={(d) => {
              cal.input.value = fmtLive(d);
              if (cal.field === "depart") {
                setDepart(d);
                // keep an earlier return coherent with the new departure
                setRet((r) => (r && startOfDay(r) < startOfDay(d) ? null : r));
              } else setRet(d);
              setCal(null);
            }}
            onClose={() => setCal(null)}
          />,
          document.body,
        )}

      {paxAnchor &&
        createPortal(
          <PaxPopover
            anchor={paxAnchor}
            pax={pax}
            onChange={setPax}
            onClose={() => setPaxAnchor(null)}
          />,
          document.body,
        )}

      {modal &&
        createPortal(
          modal.kind === "location" ? (
            <LocationModal
              stops={data.stops}
              selected={from}
              onSave={(code) => {
                if (code) setFrom(code);
                setModal(null);
              }}
              onClose={() => setModal(null)}
            />
          ) : (
            // S26 · A3 — the legacy in-widget MountainModal is retired. This branch is reached only when
            // `__transportMapModal` is set (the map-modal variant of the To field); it now opens the ONE
            // site-wide R1a selector, so no surface can serve a different mountain list again.
            <MountainSelectHandoff onClose={() => setModal(null)} />
          ),
          document.body,
        )}
    </>
  );
}

// ---- F93 ZONE DROPDOWN — the From/To picker (RW-15(d) founder override: a dropdown, not the big map).
// Options are the bookable ZONES from the legacy `route` table, constrained to the counterpart of an
// ACTIVE route — the same set live offers. Nothing is invented.
function LocationDropdown({
  kind,
  x,
  y,
  w,
  options,
  selected,
  onPick,
  onClose,
}: {
  kind: "from" | "to";
  x: number;
  y: number;
  w: number;
  options: { code: string; name: string }[];
  selected: string;
  onPick: (code: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ── S29 · F171 — MOVE FOCUS INTO THE MENU WHEN IT OPENS. ────────────────────────────────────────
  // This menu is portalled to `document.body`, i.e. it is the LAST thing in the document. Pressing Tab
  // from the From/To field therefore moved focus to whatever follows that field in the page, never into
  // the menu — so the zone list was not reachable by keyboard at all, and a focus ring on its rows was
  // unreachable by definition. Found by the S29 interaction gate: hover passed on these rows while the
  // keyboard leg could not be made to land on them from anywhere in the page.
  // Focusing the SELECTED option on open is the WAI-ARIA APG listbox behaviour, and it is what makes
  // Tab / Shift-Tab between the options work at all. It also keeps `:focus-visible` honest: this is a
  // programmatic focus, so Chromium paints the ring when the menu was opened from the KEYBOARD and
  // leaves it ringless for a mouse user — the correct outcome, not a limitation to work around.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    (
      list.querySelector<HTMLButtonElement>('[data-transport-option][aria-selected="true"]') ||
      list.querySelector<HTMLButtonElement>("[data-transport-option]")
    )?.focus();
  }, []);

  // Keep the menu inside the viewport. Guard the dimensions: some embedded/headless contexts report
  // window.innerWidth/innerHeight as 0, and clamping against 0 would slam the menu to the left edge —
  // so only clamp when we have a trustworthy (>0) viewport, otherwise anchor exactly to the field.
  const vw =
    (typeof window !== "undefined" && (window.innerWidth || document.documentElement?.clientWidth)) || 0;
  const vh =
    (typeof window !== "undefined" && (window.innerHeight || document.documentElement?.clientHeight)) || 0;
  const maxH = vh > 0 ? Math.min(320, Math.max(160, vh - y - 24)) : 320;
  const left = vw > 0 ? Math.max(8, Math.min(x, vw - w - 8)) : x;
  return (
    <div
      data-transport-dropdown={kind}
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 2500, background: "transparent" }}
    >
      <div
        ref={listRef}
        onClick={(e) => e.stopPropagation()}
        role="listbox"
        aria-label={kind === "from" ? "Pickup zone" : "Destination zone"}
        style={{
          position: "absolute",
          left,
          top: y + 4,
          width: w,
          maxHeight: maxH,
          overflowY: "auto",
          background: "#fff",
          border: "1px solid rgba(0,0,0,.25)",
          borderRadius: 8,
          boxShadow: "0 12px 28px rgba(0,0,0,.24)",
          padding: "6px 0",
          fontFamily: "var(--sg-font-web)",
        }}
      >
        {options.map((o) => (
          <button
            key={o.code}
            type="button"
            role="option"
            aria-selected={o.code === selected}
            data-transport-option
            data-code={o.code}
            onClick={() => onPick(o.code)}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              padding: "9px 16px",
              border: 0,
              background: o.code === selected ? "#f2f7ff" : "transparent",
              color: "#222",
              fontSize: 14,
              fontWeight: o.code === selected ? 600 : 400,
              cursor: "pointer",
            }}
          >
            {o.name}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- F95 PASSENGER POPOVER — live's `.memberPopover`: Adults / Children / Seniors, each a
// `.counterWidget` of `span.negative` / `span.counterValue` / `span.positive`, with `.notAllowed` on a
// control that cannot move. Those are live's own class names, so the captured CSSOM styles this.
const PAX_ROWS: Array<{ key: keyof Pax; label: string; min: number }> = [
  { key: "adult", label: "Adults", min: 1 },
  { key: "child", label: "Children", min: 0 },
  { key: "senior", label: "Seniors", min: 0 },
];
const PAX_MAX = 10;

/**
 * S29 · F173 — REBUILT AS A PROPER ATTACHED POPOVER. The founder's screenshot (room-2, 2026-07-26)
 * showed a bare floating panel, and every complaint in F173 maps to a specific line of the old
 * implementation rather than to taste:
 *
 *   "no title"                  → the panel had only an `aria-label`; nothing visible said what it was.
 *   "no Done/Apply"             → there was no confirm affordance at all, so the only way to dismiss it
 *                                 was to click into the page and hope.
 *   "no visible link to the field" → nothing tied the panel to the control that opened it.
 *   "hard shadow"               → a single `0 12px 28px rgba(0,0,0,.24)` drop.
 *   "overlaps the orange SEARCH button" → `left: Math.max(8, x - 120)`. A hand-picked 120px shove to
 *                                 the left, with no knowledge of what was underneath. MEASURED on the
 *                                 Whistler hub at 1440: the pax field sits at x 890–1029 bottom 652, so
 *                                 the panel was placed at 770–1030 / y 658–838, and SEARCH occupies
 *                                 627–813 / 696–756. The intersection is real, and visible in the
 *                                 founder's screenshot.
 *   "clips at the panel edge"   → no viewport clamping of any kind (the sibling zone dropdown already
 *                                 had it; this never got it).
 *   "no hover on ± controls"    → the steppers were `<span role="button">`: no hover state, not
 *                                 focusable, and `notAllowed` was a class that changed the cursor while
 *                                 the handler stayed live, so "disabled" was cosmetic.
 *
 * The placement is now DERIVED, not hand-tuned: prefer left-aligned to the field (which is what makes
 * it read as attached, and is why it no longer reaches back over SEARCH), flip to right-aligned if that
 * would overflow the viewport, then clamp. Same policy the zone dropdown uses.
 */
function PaxPopover({
  anchor,
  pax,
  onChange,
  onClose,
}: {
  anchor: PaxAnchorRect;
  pax: Pax;
  onChange: (p: Pax) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Focus moves into the popover when it opens — it is a dialog portalled to the end of the document,
  // so without this it is unreachable by keyboard for the same reason the zone menu was (F171).
  useEffect(() => {
    doneRef.current?.focus();
  }, []);

  const W = 268;
  // Guard the viewport dimensions the way LocationDropdown does: some headless contexts report 0, and
  // clamping against 0 would slam the panel to the left edge.
  const vw = (typeof window !== "undefined" && (window.innerWidth || document.documentElement?.clientWidth)) || 0;
  const vh = (typeof window !== "undefined" && (window.innerHeight || document.documentElement?.clientHeight)) || 0;
  // PREFERRED: left-aligned to the field. This is the placement that reads as attached and it is the
  // one that keeps the panel out of the SEARCH button's column.
  let left = anchor.left;
  if (vw > 0 && left + W > vw - 8) left = anchor.right - W; // flip to right-aligned
  if (vw > 0) left = Math.max(8, Math.min(left, vw - W - 8)); // then clamp
  const top = anchor.bottom + 10; // 10px leaves room for the caret below
  const maxH = vh > 0 ? Math.max(160, vh - top - 16) : undefined;
  // where the caret sits, in the panel's own coordinates — it points at the CENTRE of the field, so it
  // stays correct after a flip or a clamp instead of being pinned to a corner.
  const caretX = Math.max(14, Math.min(anchor.left + anchor.width / 2 - left, W - 14));

  const paxTotal = pax.adult + pax.child + pax.senior;

  return (
    <div
      data-transport-pax-popover=""
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 2600, background: "transparent" }}
    >
      <div
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="false"
        aria-label="Passengers"
        data-pax-panel
        className="popover bs-popover-bottom show"
        style={{
          position: "absolute",
          left,
          top,
          width: W,
          maxHeight: maxH,
          overflowY: "auto",
          background: "#fff",
          border: `1px solid ${RULE_LIGHT}`,
          borderRadius: 12,
          // A LAYERED shadow instead of one hard drop: a hairline contact shadow plus a wide, very soft
          // ambient one. Same total darkness, no visible hard edge — the founder's "hard shadow".
          boxShadow: "0 1px 2px rgba(16,24,40,.06), 0 8px 24px -6px rgba(16,24,40,.14), 0 18px 48px -12px rgba(16,24,40,.10)",
          fontFamily: "var(--sg-font-web)",
        }}
      >
        {/* THE VISIBLE LINK TO THE FIELD. Two stacked triangles: the outer one carries the border
            colour, the inner one punches the panel's white back over it, so the caret reads as part of
            the panel's outline rather than a floating diamond. */}
        <span
          aria-hidden
          data-pax-caret
          style={{
            position: "absolute",
            top: -9,
            left: caretX - 9,
            width: 0,
            height: 0,
            borderLeft: "9px solid transparent",
            borderRight: "9px solid transparent",
            borderBottom: `9px solid ${RULE_LIGHT}`,
          }}
        />
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: -8,
            left: caretX - 8,
            width: 0,
            height: 0,
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderBottom: "8px solid #fff",
          }}
        />

        {/* TITLE — says what the panel is, and echoes the total so the field and the panel cannot
            disagree about what has been chosen. */}
        <div
          data-pax-title
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 8,
            padding: "12px 16px 10px",
            borderBottom: `1px solid ${RULE_LIGHT}`,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: "#1f2933" }}>Passengers</span>
          <span style={{ fontSize: 12, color: GREY_TEXT }}>
            {paxTotal} {paxTotal === 1 ? "traveller" : "travellers"}
          </span>
        </div>

        <div className="popover-body" style={{ padding: "10px 16px 4px" }}>
          {PAX_ROWS.map(({ key, label, min }) => {
            const v = pax[key];
            const set = (n: number) => onChange({ ...pax, [key]: Math.min(PAX_MAX, Math.max(min, n)) });
            const atMin = v <= min;
            const atMax = v >= PAX_MAX;
            return (
              <div
                key={key}
                className="d-flex justify-content-between mb-3"
                data-pax-row={key}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}
              >
                <span style={{ fontSize: 14, color: "#1f2933" }}>{label}</span>
                <div className="counterWidget" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {/* REAL BUTTONS, genuinely `disabled` at the bounds — not a class that recolours a
                      live click target. `data-gf-stepper` opts them into the ONE shared
                      hover/focus/disabled style (F171), so their states live with every other
                      interactive control's rather than being re-invented here. */}
                  <button
                    type="button"
                    className={`negative${atMin ? " notAllowed" : ""}`}
                    aria-label={`Remove ${label}`}
                    data-pax-dec
                    data-gf-stepper
                    disabled={atMin}
                    onClick={() => set(v - 1)}
                    style={stepperBtn}
                  >
                    −
                  </button>
                  <span
                    className="counterValue"
                    data-pax-value
                    aria-live="polite"
                    style={{ minWidth: 24, textAlign: "center", fontSize: 14, fontWeight: 600, color: "#1f2933" }}
                  >
                    {v}
                  </span>
                  <button
                    type="button"
                    className={`positive${atMax ? " notAllowed" : ""}`}
                    aria-label={`Add ${label}`}
                    data-pax-inc
                    data-gf-stepper
                    disabled={atMax}
                    onClick={() => set(v + 1)}
                    style={stepperBtn}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* DONE — the confirm affordance the panel had none of. The counts are already applied as they
            change (the field's total updates live), so this CLOSES rather than commits; labelled "Done"
            and not "Apply" for exactly that reason. */}
        <div style={{ padding: "8px 16px 14px", borderTop: `1px solid ${RULE_LIGHT}`, display: "flex", justifyContent: "flex-end" }}>
          <button
            ref={doneRef}
            type="button"
            data-pax-done
            data-gf-stepper
            onClick={onClose}
            style={{
              border: `1px solid ${ORANGE_BRAND}`,
              background: ORANGE_BRAND,
              color: "#fff",
              borderRadius: 999,
              padding: "7px 20px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// F173 tokens. The brand orange and the hairline are the same values the rest of the funnel chrome uses
// (BookingChrome's ORANGE / RULE), restated locally so this component does not import the funnel to draw
// a popover on the hub.
const ORANGE_BRAND = "#f15a22";
const RULE_LIGHT = "#e3e8ef";
const GREY_TEXT = "#6b7684";
const stepperBtn: React.CSSProperties = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  border: `1px solid ${RULE_LIGHT}`,
  background: "#fff",
  color: "#1f2933",
  fontSize: 16,
  lineHeight: 1,
  cursor: "pointer",
  userSelect: "none",
  fontFamily: "inherit",
  padding: 0,
};

// ---- TransporLocationFilter port: pickup/dropoff dropdowns + PortMap (retained behind the flag) ----
function LocationModal({
  stops,
  selected,
  onSave,
  onClose,
}: {
  stops: TransportLocation[];
  selected: string;
  onSave: (code: string) => void;
  onClose: () => void;
}) {
  const [pickup, setPickup] = useState(selected);
  const marker = stops.find((s) => s.code === pickup);
  const markers: PortMapMarker[] = stops
    .filter((s) => s.lat != null && s.lng != null)
    .map((s) => ({ lat: s.lat!, lng: s.lng!, name: s.name, key: s.code, flag: /airport|yvr/i.test(s.name) ? "plane" : "other" }));
  return (
    <ModalShell title="TRANSPORT LOCATION FILTER" onClose={onClose} data-modal="location">
      <div style={{ display: "flex", gap: 16, minHeight: 340 }}>
        <div style={{ width: "45%" }}>
          <p style={{ fontSize: 13, color: "#555" }}>Choose from the map or dropdown to set your pickup. Hit continue to skip.</p>
          <label style={{ fontWeight: 600, fontSize: 13 }}>Pickup Location</label>
          <select
            aria-label="Pickup Location"
            value={pickup}
            onChange={(e) => setPickup(e.target.value)}
            style={{ width: "100%", padding: 8, margin: "6px 0 16px", borderRadius: 6, border: "1px solid #ccc" }}
          >
            {stops.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onClose} style={btn("outline")}>
              Cancel
            </button>
            <button type="button" onClick={() => onSave(pickup)} style={btn("primary")}>
              SAVE SELECTION
            </button>
          </div>
        </div>
        <div style={{ width: "55%", minHeight: 340 }}>
          <PortMap
            markers={markers}
            mode="default"
            center={marker && marker.lat != null ? { lat: marker.lat, lng: marker.lng! } : undefined}
            zoom={10}
            surface="transport-location-filter"
          />
        </div>
      </div>
    </ModalShell>
  );
}

// ---- S26 · A3 — MountainSelection HANDOFF. The widget used to render its OWN plain-list mountain modal
// here (list + PortMap, no search / logos / country accordions / Details), which the A2 mount audit caught
// as one of the two old selectors still reachable after S25. It renders nothing itself now: it asks the
// site-wide layer to open the R1a selector and immediately drops its own modal state, so exactly one
// selector is ever on screen.
function MountainSelectHandoff({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    openMountainSelect(null); // neutral → the mountain hub, as this modal's entries already resolved
    onClose();
  }, [onClose]);
  return null;
}
